// P2P Marketplace — Zelle/Wise client-to-client exchange.
// Creator workflow + Claimer workflow + Live order list.
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useExchangeRate, formatUSD } from "./lib/currency";
import {
  RefreshCw, Plus, Clock, X, Send, CheckCircle, Upload, AlertTriangle, Star,
  ArrowLeft, DollarSign, TrendingUp, Users, Award
} from "lucide-react";
import HtgP2PExchange from "./HtgP2PExchange";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const fmt2 = (n) => Number(n || 0).toFixed(2);

const STATUS_LABEL = {
  open: "Ouverte",
  claimed: "Prise en charge",
  accepted_zelle: "Acceptée — En attente infos",
  accepted_wise: "Acceptée — En attente infos",
  info_provided: "Infos fournies — En attente preuve",
  proof_uploaded: "Preuve envoyée — En attente validation",
  validated: "Validée",
  cancelled: "Annulée",
};

const STATUS_COLOR = {
  open: "#0EA5E9",
  claimed: "#F59E0B",
  accepted_zelle: "#8B5CF6",
  accepted_wise: "#8B5CF6",
  info_provided: "#A855F7",
  proof_uploaded: "#EC4899",
  validated: "#16A34A",
  cancelled: "#94A3B8",
};

const tokenKey = "client_token";
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem(tokenKey)}` });

export default function P2PMarketplace({ clientUser, onBack }) {
  const exchangeRate = useExchangeRate();
  const [tab, setTab] = useState("zelle");                // zelle | wise
  const [view, setView] = useState("list");               // list | mine | detail
  const [openOrders, setOpenOrders] = useState([]);
  const [myOrders, setMyOrders] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);

  // Dedicated P2P balances (separated from main balance) — TWO buckets, no conversion
  const [p2pHtg, setP2pHtg] = useState(0);
  const [p2pUsd, setP2pUsd] = useState(0);
  const [p2pLedgerOpen, setP2pLedgerOpen] = useState(false);
  const [p2pLedger, setP2pLedger] = useState([]);

  // P2P Recharge
  const [showRecharge, setShowRecharge] = useState(false);
  const [rechargeMethods, setRechargeMethods] = useState([]);
  const [rechargeMethodKey, setRechargeMethodKey] = useState("");
  const [rechargeAmount, setRechargeAmount] = useState("");
  const [rechargeNote, setRechargeNote] = useState("");
  const [rechargeProof, setRechargeProof] = useState("");
  const [rechargeUploading, setRechargeUploading] = useState(false);
  const [rechargeSubmitting, setRechargeSubmitting] = useState(false);
  const [myDeposits, setMyDeposits] = useState([]);
  const [showMyDeposits, setShowMyDeposits] = useState(false);

  // Convertisseur HTG↔USD
  const [showConvert, setShowConvert] = useState(false);
  const [convertRates, setConvertRates] = useState({ htg_to_usd_rate: 160, usd_to_htg_rate: 140 });
  const [convertFrom, setConvertFrom] = useState("HTG"); // HTG → USD or USD → HTG
  const [convertAmount, setConvertAmount] = useState("");
  const [convertSubmitting, setConvertSubmitting] = useState(false);

  // Retrait P2P
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [withdrawMethods, setWithdrawMethods] = useState({ HTG: [], USD: [] });
  const [withdrawCurrency, setWithdrawCurrency] = useState("HTG");
  const [withdrawMethod, setWithdrawMethod] = useState("");
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [withdrawAccount, setWithdrawAccount] = useState("");
  const [withdrawNote, setWithdrawNote] = useState("");
  const [withdrawSubmitting, setWithdrawSubmitting] = useState(false);
  const [myWithdrawals, setMyWithdrawals] = useState([]);
  const [showMyWithdrawals, setShowMyWithdrawals] = useState(false);

  // P2P Sell/Buy Offers (Annonces marketplace)
  const [offers, setOffers] = useState([]);
  const [myOffers, setMyOffers] = useState([]);
  const [offerFilterDirection, setOfferFilterDirection] = useState(""); // ""|sell|buy
  const [offerFilterQuery, setOfferFilterQuery] = useState("");
  const [showOfferCreate, setShowOfferCreate] = useState(false);
  const [offerKind, setOfferKind] = useState("wise");
  const [offerDirection, setOfferDirection] = useState("sell");
  const [offerAmount, setOfferAmount] = useState("");
  const [offerRate, setOfferRate] = useState("147");
  const [offerAvailability, setOfferAvailability] = useState("Immédiat");
  const [offerSubmitting, setOfferSubmitting] = useState(false);
  const [editOffer, setEditOffer] = useState(null);

  // Create form
  const [showCreate, setShowCreate] = useState(false);
  const [createAmount, setCreateAmount] = useState("");

  // Claim form
  const [showClaim, setShowClaim] = useState(false);
  const [claimRate, setClaimRate] = useState("");
  const [claimFee, setClaimFee] = useState("0");

  // Info form
  const [showInfo, setShowInfo] = useState(false);
  const [infoEmail, setInfoEmail] = useState("");
  const [infoFirst, setInfoFirst] = useState("");
  const [infoLast, setInfoLast] = useState("");
  const [infoTag, setInfoTag] = useState("");

  // Proof
  const [proofUrl, setProofUrl] = useState("");
  const [uploadingProof, setUploadingProof] = useState(false);

  // Review
  const [reviewStars, setReviewStars] = useState(5);
  const [reviewComment, setReviewComment] = useState("");

  const loadOpen = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/p2p/orders/open?kind=${tab}`, { headers: auth() });
      setOpenOrders(r.data.orders || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  }, [tab]);

  const loadRechargeMethods = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/p2p/payment-methods`, { headers: auth() });
      setRechargeMethods(r.data.methods || []);
    } catch (e) { if (process.env.NODE_ENV === "development") console.debug("[P2P] silent error:", e?.message); }
  }, []);

  const loadMyDeposits = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/p2p/deposits/mine?limit=30`, { headers: auth() });
      setMyDeposits(r.data.deposits || []);
    } catch (e) { if (process.env.NODE_ENV === "development") console.debug("[P2P] silent error:", e?.message); }
  }, []);

  const loadConvertRates = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/p2p/conversion-rates`, { headers: auth() });
      setConvertRates(r.data.rates || { htg_to_usd_rate: 160, usd_to_htg_rate: 140 });
    } catch (e) { if (process.env.NODE_ENV === "development") console.debug("[P2P] silent error:", e?.message); }
  }, []);

  const loadWithdrawMethods = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/p2p/withdrawal-methods`, { headers: auth() });
      setWithdrawMethods(r.data.methods || { HTG: [], USD: [] });
    } catch (e) { if (process.env.NODE_ENV === "development") console.debug("[P2P] silent error:", e?.message); }
  }, []);

  const loadMyWithdrawals = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/p2p/withdrawals/mine?limit=30`, { headers: auth() });
      setMyWithdrawals(r.data.withdrawals || []);
    } catch (e) { if (process.env.NODE_ENV === "development") console.debug("[P2P] silent error:", e?.message); }
  }, []);

  const loadOffers = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.set("kind", tab);
      params.set("only_open", "true");
      if (offerFilterDirection) params.set("direction", offerFilterDirection);
      if (offerFilterQuery) params.set("q", offerFilterQuery);
      const r = await axios.get(`${API}/p2p/sell-offers?${params}`, { headers: auth() });
      setOffers(r.data.offers || []);
    } catch (e) { if (process.env.NODE_ENV === "development") console.debug("[P2P] silent error:", e?.message); }
  }, [tab, offerFilterDirection, offerFilterQuery]);

  const loadMyOffers = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/p2p/sell-offers/mine?limit=50`, { headers: auth() });
      setMyOffers(r.data.offers || []);
    } catch (e) { if (process.env.NODE_ENV === "development") console.debug("[P2P] silent error:", e?.message); }
  }, []);

  const loadP2pBalance = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/p2p/balance`, { headers: auth() });
      const nextHtg = Number(r.data?.balance_htg ?? r.data?.balance) || 0;
      const nextUsd = Number(r.data?.balance_usd) || 0;
      setP2pHtg(prev => {
        if (nextHtg > prev + 0.01 && prev > 0) {
          toast.success(`💱 +${(nextHtg - prev).toFixed(2)} G sur Solde P2P`, { duration: 5000 });
        }
        return nextHtg;
      });
      setP2pUsd(prev => {
        if (nextUsd > prev + 0.01 && prev > 0) {
          toast.success(`💵 +${(nextUsd - prev).toFixed(2)} $ sur Solde P2P USD`, { duration: 5000 });
        }
        return nextUsd;
      });
      setP2pLedger(r.data?.ledger || []);
    } catch (e) { if (process.env.NODE_ENV === "development") console.debug("[P2P] silent error:", e?.message); }
  }, []);

  const loadMine = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/p2p/orders/mine?kind=${tab}`, { headers: auth() });
      setMyOrders(r.data.orders || []);
    } catch (e) { if (process.env.NODE_ENV === "development") console.debug("[P2P] silent error:", e?.message); }
  }, [tab]);

  const loadOrder = useCallback(async (id) => {
    try {
      const r = await axios.get(`${API}/p2p/orders/${id}`, { headers: auth() });
      setOrder(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  }, []);

  useEffect(() => {
    loadOpen(); loadMine(); loadP2pBalance();
    loadRechargeMethods(); loadMyDeposits();
    loadConvertRates(); loadWithdrawMethods(); loadMyWithdrawals();
    loadOffers(); loadMyOffers();
  }, [loadOpen, loadMine, loadP2pBalance, loadRechargeMethods, loadMyDeposits, loadConvertRates, loadWithdrawMethods, loadMyWithdrawals, loadOffers, loadMyOffers]);

  // Auto-refresh open orders + P2P balance every 10s
  useEffect(() => {
    const t = setInterval(() => {
      if (document.visibilityState === "visible") {
        loadOpen();
        loadP2pBalance();
        if (selectedId) loadOrder(selectedId);
      }
    }, 10000);
    return () => clearInterval(t);
  }, [loadOpen, loadOrder, loadP2pBalance, selectedId]);

  const open = async (id) => { setSelectedId(id); await loadOrder(id); setView("detail"); };

  const handleRechargeFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 6 * 1024 * 1024) { toast.error("Image > 6 Mo"); return; }
    setRechargeUploading(true);
    const reader = new FileReader();
    reader.onload = () => { setRechargeProof(reader.result); setRechargeUploading(false); };
    reader.onerror = () => { toast.error("Erreur lecture fichier"); setRechargeUploading(false); };
    reader.readAsDataURL(f);
  };

  const handleSubmitRecharge = async () => {
    const m = rechargeMethods.find(x => x.key === rechargeMethodKey);
    if (!m) { toast.error("Méthode requise"); return; }
    const amt = parseFloat(rechargeAmount);
    if (!amt || amt <= 0) { toast.error("Montant invalide"); return; }
    if (!rechargeProof) { toast.error("Preuve de paiement requise"); return; }
    setRechargeSubmitting(true);
    try {
      const r = await axios.post(`${API}/p2p/deposit`, {
        amount: amt,
        currency: m.currency,
        method: m.key,
        proof_url: rechargeProof,
        note: rechargeNote.trim(),
      }, { headers: auth() });
      toast.success(r.data?.message || "Demande envoyée. L'admin va vérifier votre preuve.", { duration: 6000 });
      setShowRecharge(false);
      setRechargeMethodKey(""); setRechargeAmount(""); setRechargeNote(""); setRechargeProof("");
      loadMyDeposits();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setRechargeSubmitting(false);
    }
  };

  const handleSubmitConvert = async () => {
    const amt = parseFloat(convertAmount);
    if (!amt || amt <= 0) { toast.error("Montant invalide"); return; }
    setConvertSubmitting(true);
    try {
      const r = await axios.post(`${API}/p2p/convert`,
        { from_currency: convertFrom, amount: amt },
        { headers: { ...auth(), "Content-Type": "application/json" } });
      const fromSuf = convertFrom === "USD" ? "$" : "G";
      const toSuf = convertFrom === "USD" ? "G" : "$";
      toast.success(`✓ ${amt.toFixed(2)} ${fromSuf} → ${r.data.amount_out.toFixed(2)} ${toSuf} (taux ${r.data.rate})`, { duration: 6000 });
      setShowConvert(false); setConvertAmount("");
      loadP2pBalance();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setConvertSubmitting(false); }
  };

  const handleSubmitWithdraw = async () => {
    const amt = parseFloat(withdrawAmount);
    if (!amt || amt <= 0) { toast.error("Montant invalide"); return; }
    if (!withdrawMethod) { toast.error("Méthode requise"); return; }
    const methodMeta = (withdrawMethods[withdrawCurrency] || []).find(m => m.key === withdrawMethod);
    if (methodMeta?.needs_account && !withdrawAccount.trim()) {
      toast.error(`${methodMeta.account_label} requis(e)`); return;
    }
    setWithdrawSubmitting(true);
    try {
      const r = await axios.post(`${API}/p2p/withdrawals`, {
        currency: withdrawCurrency,
        amount: amt,
        method: withdrawMethod,
        account: withdrawAccount.trim(),
        note: withdrawNote.trim(),
      }, { headers: { ...auth(), "Content-Type": "application/json" } });
      toast.success("Retrait enregistré — fonds verrouillés", { duration: 4000 });
      // Open WhatsApp pre-filled
      if (r.data?.whatsapp_url) {
        window.open(r.data.whatsapp_url, "_blank", "noopener,noreferrer");
      }
      setShowWithdraw(false);
      setWithdrawAmount(""); setWithdrawAccount(""); setWithdrawNote(""); setWithdrawMethod("");
      loadP2pBalance(); loadMyWithdrawals();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setWithdrawSubmitting(false); }
  };

  const handleCreateOffer = async () => {
    const amt = parseFloat(offerAmount); const rt = parseFloat(offerRate);
    if (!amt || amt < 5) { toast.error("Montant minimum 5 USD"); return; }
    if (!rt || rt < 50 || rt > 500) { toast.error("Taux invalide (50-500)"); return; }
    setOfferSubmitting(true);
    try {
      const url = editOffer ? `${API}/p2p/sell-offers/${editOffer.id}` : `${API}/p2p/sell-offers`;
      const method = editOffer ? "patch" : "post";
      const body = editOffer
        ? { amount_usd: amt, rate: rt, availability: offerAvailability.trim() }
        : { kind: offerKind, direction: offerDirection, amount_usd: amt, rate: rt, availability: offerAvailability.trim() };
      const r = await axios[method](url, body, { headers: { ...auth(), "Content-Type": "application/json" } });
      toast.success(editOffer ? "Annonce mise à jour" : `Annonce publiée — caution ${r.data.caution_htg} G verrouillée`, { duration: 5000 });
      setShowOfferCreate(false); setEditOffer(null);
      setOfferAmount(""); setOfferRate("147"); setOfferAvailability("Immédiat");
      loadOffers(); loadMyOffers(); loadP2pBalance();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setOfferSubmitting(false); }
  };

  const handleDeleteOffer = async (id) => {
    if (!window.confirm) {} // not used — direct delete
    try {
      const r = await axios.delete(`${API}/p2p/sell-offers/${id}`, { headers: auth() });
      toast.success(`Annonce supprimée — caution ${r.data.caution_refunded_htg} G remboursée`);
      loadOffers(); loadMyOffers(); loadP2pBalance();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const handleClaimOffer = async (offer) => {
    try {
      const r = await axios.post(`${API}/p2p/sell-offers/${offer.id}/claim`, {},
        { headers: { ...auth(), "Content-Type": "application/json" } });
      toast.success(`✓ Annonce réservée 50 min — contactez ${r.data.publisher_name} via WhatsApp`, { duration: 7000 });
      // Open WhatsApp
      const phone = (r.data.publisher_phone || "").replace(/\D/g, "");
      if (phone) {
        const msg = encodeURIComponent(`Bonjour ${r.data.publisher_name}, je viens de commander votre annonce ${offer.kind.toUpperCase()} ${offer.amount_usd}$ @ ${offer.rate} G. Pouvons-nous procéder ?`);
        window.open(`https://wa.me/${phone}?text=${msg}`, "_blank", "noopener,noreferrer");
      }
      loadOffers(); loadMyOffers();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const handleFulfillOffer = async (id) => {
    try {
      const r = await axios.post(`${API}/p2p/sell-offers/${id}/fulfill`, {},
        { headers: { ...auth(), "Content-Type": "application/json" } });
      toast.success(`✓ Annonce honorée — caution ${r.data.caution_refunded_htg} G remboursée`);
      loadOffers(); loadMyOffers(); loadP2pBalance();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const handleCreate = async () => {
    const amt = parseFloat(createAmount);
    if (!amt || amt < 5) { toast.error("Montant minimum 5 $"); return; }
    try {
      const r = await axios.post(`${API}/p2p/orders`, { kind: tab, amount_usd: amt }, { headers: auth() });
      toast.success(`Commande ${tab.toUpperCase()} créée — Chronomètre 50 min lancé`);
      setShowCreate(false); setCreateAmount("");
      loadOpen(); loadMine();
      open(r.data.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const handleClaim = async () => {
    const r = parseFloat(claimRate);
    const f = parseFloat(claimFee || 0);
    if (!r || r <= 0) { toast.error("Taux requis"); return; }
    try {
      await axios.post(`${API}/p2p/orders/${order.id}/claim`, { rate: r, fee_pct: f }, { headers: auth() });
      toast.success("Le client est informé");
      setShowClaim(false); setClaimRate(""); setClaimFee("0");
      loadOrder(order.id); loadOpen(); loadMine(); loadP2pBalance();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const handleRespond = async (action) => {
    if (action === "refuse" && !window.confirm("Refuser la prise en charge ? L'autre client sera remboursé.")) return;
    try {
      await axios.post(`${API}/p2p/orders/${order.id}/respond`, { action }, { headers: auth() });
      toast.success(action === "accept" ? "Acceptée" : "Refusée");
      loadOrder(order.id); loadMine(); loadP2pBalance();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const handleSubmitInfo = async () => {
    if (!infoEmail.includes("@") || !infoFirst.trim() || !infoLast.trim()) { toast.error("Email + prénom + nom requis"); return; }
    try {
      await axios.post(`${API}/p2p/orders/${order.id}/info`, {
        email: infoEmail.trim(), first_name: infoFirst.trim(), last_name: infoLast.trim(),
        wise_tag: infoTag.trim(),
      }, { headers: auth() });
      toast.success("Infos envoyées");
      setShowInfo(false); setInfoEmail(""); setInfoFirst(""); setInfoLast(""); setInfoTag("");
      loadOrder(order.id); loadP2pBalance();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const handleProofFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploadingProof(true);
    const reader = new FileReader();
    reader.onload = () => { setProofUrl(reader.result); setUploadingProof(false); };
    reader.onerror = () => { toast.error("Erreur lecture fichier"); setUploadingProof(false); };
    reader.readAsDataURL(f);
  };

  const handleSubmitProof = async () => {
    if (!proofUrl) { toast.error("Joindre la preuve d'abord"); return; }
    try {
      await axios.post(`${API}/p2p/orders/${order.id}/proof`, { proof_url: proofUrl }, { headers: auth() });
      toast.success("Preuve envoyée");
      setProofUrl("");
      loadOrder(order.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const handleValidate = async () => {
    if (!window.confirm("Confirmer la validation ? Les fonds seront libérés.")) return;
    try {
      await axios.post(`${API}/p2p/orders/${order.id}/validate`, {}, { headers: auth() });
      toast.success("Transaction validée — fonds transférés");
      loadOrder(order.id); loadMine(); loadP2pBalance();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const handleCancel = async () => {
    if (!window.confirm("Annuler cette commande ?")) return;
    try {
      await axios.post(`${API}/p2p/orders/${order.id}/cancel`, {}, { headers: auth() });
      toast.success("Annulée");
      loadOrder(order.id); loadMine(); loadOpen();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const handleReview = async () => {
    try {
      await axios.post(`${API}/p2p/orders/${order.id}/review`, { stars: reviewStars, comment: reviewComment }, { headers: auth() });
      toast.success("Merci pour votre note !");
      setReviewComment("");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const isCreator = order && clientUser && order.creator_id === clientUser.id;
  const isClaimer = order && clientUser && order.claimer_id === clientUser.id;

  const remainingMin = (o) => {
    if (!o?.expires_at) return null;
    const ms = new Date(o.expires_at).getTime() - Date.now();
    if (ms <= 0) return 0;
    return Math.ceil(ms / 60000);
  };

  return (
    <div data-testid="p2p-marketplace" style={{ maxWidth: 880, margin: "0 auto", padding: "0.7rem" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.6rem", flexWrap: "wrap", gap: 6 }}>
        <button onClick={onBack} className="btn btn-sm" style={{ background: "#E2E8F0" }} data-testid="p2p-back-btn">
          <ArrowLeft size={14} /> Retour
        </button>
        <h2 style={{ fontSize: "1.05rem", margin: 0, fontWeight: 800 }}>💱 Marketplace P2P — Zelle / Wise</h2>
        <button onClick={() => setShowCreate(true)} className="btn btn-sm btn-primary" data-testid="p2p-create-btn">
          <Plus size={14} /> Commander
        </button>
      </div>

      {/* === Dedicated P2P balances card — TWO buckets (HTG + USD) — no conversion === */}
      <div style={{
        background: "linear-gradient(135deg, #4338CA 0%, #6D28D9 100%)",
        padding: "0.9rem 1rem", borderRadius: 12, color: "#FFF",
        marginBottom: "0.7rem",
      }} data-testid="p2p-balance-card">
        <div style={{ fontSize: "0.7rem", opacity: 0.85, fontWeight: 700, letterSpacing: 0.5, marginBottom: 8 }}>
          SOLDE P2P MARKETPLACE
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 8 }}>
          {/* USD bucket */}
          <div style={{
            flex: "1 1 200px", background: "rgba(255,255,255,0.13)",
            borderRadius: 10, padding: "0.6rem 0.8rem",
            borderLeft: "3px solid #34D399",
          }} data-testid="p2p-balance-usd-card">
            <div style={{ fontSize: "0.66rem", opacity: 0.85, fontWeight: 700, letterSpacing: 0.5 }}>DOLLARS (USD)</div>
            <div style={{ fontSize: "1.45rem", fontWeight: 900, marginTop: 2 }} data-testid="p2p-balance-usd">
              {Number(p2pUsd).toFixed(2)} $
            </div>
          </div>
          {/* HTG bucket */}
          <div style={{
            flex: "1 1 200px", background: "rgba(255,255,255,0.13)",
            borderRadius: 10, padding: "0.6rem 0.8rem",
            borderLeft: "3px solid #FBBF24",
          }} data-testid="p2p-balance-htg-card">
            <div style={{ fontSize: "0.66rem", opacity: 0.85, fontWeight: 700, letterSpacing: 0.5 }}>GOURDES (GDES)</div>
            <div style={{ fontSize: "1.45rem", fontWeight: 900, marginTop: 2 }} data-testid="p2p-balance-htg">
              {Number(p2pHtg).toLocaleString("fr-FR", { maximumFractionDigits: 2 })} G
            </div>
          </div>
        </div>
        <div style={{ fontSize: "0.7rem", opacity: 0.85, marginBottom: 8 }}>
          Indépendant du solde principal • Aucune conversion automatique • Recharge gérée par l'admin
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          <button onClick={() => setShowRecharge(true)}
            className="btn btn-sm" style={{ background: "#16A34A", color: "white", fontWeight: 800, fontSize: "0.78rem" }}
            data-testid="p2p-recharge-btn">
            <Plus size={13} /> Recharger
          </button>
          <button onClick={() => { setConvertFrom("HTG"); setConvertAmount(""); setShowConvert(true); }}
            className="btn btn-sm" style={{ background: "#0EA5E9", color: "white", fontWeight: 800, fontSize: "0.78rem" }}
            data-testid="p2p-convert-btn">
            ⇄ Convertir
          </button>
          <button onClick={() => { setWithdrawCurrency("HTG"); setWithdrawMethod(""); setWithdrawAmount(""); setWithdrawAccount(""); setWithdrawNote(""); setShowWithdraw(true); }}
            className="btn btn-sm" style={{ background: "#DC2626", color: "white", fontWeight: 800, fontSize: "0.78rem" }}
            data-testid="p2p-withdraw-btn">
            ↗ Retirer
          </button>
          <button onClick={() => setP2pLedgerOpen(!p2pLedgerOpen)}
            className="btn btn-sm" style={{ background: "rgba(255,255,255,0.2)", color: "white", fontWeight: 700, fontSize: "0.78rem" }}
            data-testid="p2p-ledger-toggle">
            {p2pLedgerOpen ? "Masquer historique" : "Voir historique"}
          </button>
          <button onClick={() => setShowMyDeposits(!showMyDeposits)}
            className="btn btn-sm" style={{ background: "rgba(255,255,255,0.18)", color: "white", fontWeight: 700, fontSize: "0.72rem" }}
            data-testid="p2p-my-deposits-toggle">
            {showMyDeposits ? "Masquer recharges" : `Mes recharges (${myDeposits.length})`}
          </button>
          <button onClick={() => setShowMyWithdrawals(!showMyWithdrawals)}
            className="btn btn-sm" style={{ background: "rgba(255,255,255,0.18)", color: "white", fontWeight: 700, fontSize: "0.72rem" }}
            data-testid="p2p-my-withdrawals-toggle">
            {showMyWithdrawals ? "Masquer retraits" : `Mes retraits (${myWithdrawals.length})`}
          </button>
        </div>
      </div>

      {/* P2P Ledger collapsible */}
      {p2pLedgerOpen && (
        <div style={{ background: "#F8FAFC", padding: "0.5rem 0.7rem", borderRadius: 10, marginBottom: 8, maxHeight: 220, overflowY: "auto" }} data-testid="p2p-ledger-list">
          {p2pLedger.length === 0 ? (
            <div style={{ textAlign: "center", padding: "0.8rem", color: "#94A3B8", fontSize: "0.78rem" }}>Aucun mouvement</div>
          ) : p2pLedger.map(e => {
            const cur = (e.currency || "HTG").toUpperCase();
            const sign = e.delta > 0 ? "+" : "";
            const amount = cur === "USD" ? `${sign}${Number(e.delta).toFixed(2)} $` : `${sign}${Number(e.delta).toLocaleString("fr-FR", { maximumFractionDigits: 2 })} G`;
            return (
              <div key={e.id} style={{ padding: "0.35rem 0", borderBottom: "1px solid #E2E8F0", fontSize: "0.78rem", display: "flex", justifyContent: "space-between", gap: 6 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700 }}>{e.reason}</div>
                  <div style={{ fontSize: "0.68rem", color: "#94A3B8" }}>
                    <span style={{ display: "inline-block", padding: "1px 6px", borderRadius: 999, background: cur === "USD" ? "#D1FAE5" : "#FEF3C7", color: cur === "USD" ? "#065F46" : "#92400E", fontWeight: 700, marginRight: 4 }}>{cur}</span>
                    {(e.created_at || "").slice(0, 19).replace("T", " ")}
                  </div>
                </div>
                <div style={{ fontWeight: 800, color: e.delta > 0 ? "#16A34A" : "#DC2626", whiteSpace: "nowrap" }}>
                  {amount}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* My Withdrawals history */}
      {showMyWithdrawals && (
        <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", padding: "0.5rem 0.7rem", borderRadius: 10, marginBottom: 8, maxHeight: 220, overflowY: "auto" }} data-testid="p2p-my-withdrawals-list">
          {myWithdrawals.length === 0 ? (
            <div style={{ textAlign: "center", padding: "0.8rem", color: "#94A3B8", fontSize: "0.78rem" }}>
              Aucun retrait. Cliquez sur « Retirer » pour démarrer.
            </div>
          ) : myWithdrawals.map(w => {
            const status = w.status || "pending";
            const color = status === "approved" ? "#16A34A" : status === "rejected" ? "#DC2626" : status === "processing" ? "#3B82F6" : "#F59E0B";
            const label = status === "approved" ? "Validé"
              : status === "rejected" ? `Refusé${w.rejection_reason ? ` — ${w.rejection_reason}` : ""}`
              : status === "processing" ? "En cours"
              : "En attente";
            const sfx = w.currency === "USD" ? "$" : "G";
            return (
              <div key={w.id} style={{ padding: "0.4rem 0", borderBottom: "1px solid #FEE2E2", fontSize: "0.78rem" }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 6 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700 }}>
                      {w.amount.toFixed(2)} {sfx} <span style={{ color: "#7C3AED" }}>· {w.method_label}</span>
                    </div>
                    <div style={{ fontSize: "0.68rem", color: "#94A3B8" }}>
                      {w.account && `→ ${w.account} · `}{(w.created_at || "").slice(0, 19).replace("T", " ")}
                    </div>
                  </div>
                  <div style={{ fontWeight: 800, color, whiteSpace: "nowrap", fontSize: "0.72rem" }}>{label}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* My Recharges history */}
      {showMyDeposits && (
        <div style={{ background: "#FAF5FF", border: "1px solid #E9D5FF", padding: "0.5rem 0.7rem", borderRadius: 10, marginBottom: 8, maxHeight: 220, overflowY: "auto" }} data-testid="p2p-my-deposits-list">
          {myDeposits.length === 0 ? (
            <div style={{ textAlign: "center", padding: "0.8rem", color: "#94A3B8", fontSize: "0.78rem" }}>
              Aucune recharge envoyée. Cliquez sur « Recharger » pour démarrer.
            </div>
          ) : myDeposits.map(d => {
            const status = d.status || "pending";
            const color = status === "approved" ? "#16A34A" : status === "rejected" ? "#DC2626" : "#F59E0B";
            const label = status === "approved" ? "Approuvée"
              : status === "rejected" ? `Refusée${d.rejection_reason ? ` — ${d.rejection_reason}` : ""}`
              : status === "validating" ? "En cours"
              : "En attente";
            return (
              <div key={d.id} style={{ padding: "0.4rem 0", borderBottom: "1px solid #F1E8FF", fontSize: "0.78rem" }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 6 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700 }}>
                      {d.amount_input} {d.currency_input} <span style={{ color: "#7C3AED" }}>· {d.method || "—"}</span>
                    </div>
                    <div style={{ fontSize: "0.68rem", color: "#94A3B8" }}>
                      {(d.created_at || "").slice(0, 19).replace("T", " ")}
                    </div>
                  </div>
                  <div style={{ fontWeight: 800, color, whiteSpace: "nowrap" }}>{label}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Kind tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: "0.6rem", flexWrap: "wrap" }}>
        {["zelle", "wise", "htg"].map(k => (
          <button key={k} onClick={() => { setTab(k); setView("list"); }} className="btn btn-sm" data-testid={`p2p-tab-${k}`}
            style={{ flex: "1 1 30%", background: tab === k ? "#0F172A" : "#E2E8F0", color: tab === k ? "white" : "#0F172A", fontWeight: 700, textTransform: "uppercase", fontSize: "0.85rem" }}>
            {k === "htg" ? "Gourdes P2P" : k}
          </button>
        ))}
      </div>

      {/* HTG P2P (MonCash ↔ NatCash) — dedicated panel */}
      {tab === "htg" && (
        <HtgP2PExchange clientId={clientUser?.id} />
      )}

      {/* List/Mine/Annonces sub-tabs — hidden for htg */}
      {tab !== "htg" && view !== "detail" && (
        <div style={{ display: "flex", gap: 4, marginBottom: "0.6rem", flexWrap: "wrap" }}>
          <button onClick={() => setView("list")} className="btn btn-sm" data-testid="p2p-view-open"
            style={{ flex: "1 1 130px", background: view === "list" ? "#16A34A" : "#F1F5F9", color: view === "list" ? "white" : "#0F172A", fontWeight: 700 }}>
            Commandes ({openOrders.length})
          </button>
          <button onClick={() => setView("offers")} className="btn btn-sm" data-testid="p2p-view-offers"
            style={{ flex: "1 1 130px", background: view === "offers" ? "#0EA5E9" : "#F1F5F9", color: view === "offers" ? "white" : "#0F172A", fontWeight: 700 }}>
            📢 Annonces ({offers.length})
          </button>
          <button onClick={() => setView("mine")} className="btn btn-sm" data-testid="p2p-view-mine"
            style={{ flex: "1 1 130px", background: view === "mine" ? "#7C3AED" : "#F1F5F9", color: view === "mine" ? "white" : "#0F172A", fontWeight: 700 }}>
            Mes ({myOrders.length})
          </button>
          <button onClick={() => { loadOpen(); loadMine(); loadOffers(); loadMyOffers(); }} className="btn btn-sm" style={{ background: "#E0E7FF" }} data-testid="p2p-refresh-btn">
            <RefreshCw size={13} />
          </button>
        </div>
      )}

      {/* List view */}
      {tab !== "htg" && view === "list" && (
        <div data-testid="p2p-open-list">
          {loading && <div style={{ textAlign: "center", padding: "1rem", color: "#94A3B8" }}>Chargement…</div>}
          {!loading && openOrders.length === 0 && (
            <div style={{ textAlign: "center", padding: "2rem 1rem", color: "#94A3B8", border: "1px dashed #E2E8F0", borderRadius: 10 }}>
              Aucune commande {tab.toUpperCase()} ouverte. Créez la vôtre !
            </div>
          )}
          {openOrders.map(o => {
            const mine = clientUser && o.creator_id === clientUser.id;
            const rem = remainingMin(o);
            return (
              <div key={o.id} className="card" style={{ marginBottom: 6, padding: "0.6rem 0.8rem", borderLeft: `4px solid ${STATUS_COLOR[o.status]}` }} data-testid={`p2p-order-${o.id}`}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
                  <div>
                    <div style={{ fontWeight: 800 }}>{o.creator_name || "Client"} demande <span style={{ color: "#16A34A" }}>{fmt2(o.amount_usd)} $</span></div>
                    <div style={{ fontSize: "0.72rem", color: "#94A3B8" }}>{(o.created_at || "").slice(0, 16).replace("T", " ")}</div>
                  </div>
                  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    <span style={{ fontSize: "0.72rem", color: rem === 0 ? "#DC2626" : "#0F172A", fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 3 }}>
                      <Clock size={11} /> {rem === null ? "—" : `${rem} min`}
                    </span>
                    {mine ? (
                      <span style={{ fontSize: "0.7rem", padding: "2px 8px", borderRadius: 999, background: "#F1F5F9", color: "#64748B", fontWeight: 700 }}>La vôtre</span>
                    ) : (
                      <button onClick={() => open(o.id)} className="btn btn-sm" style={{ background: "#16A34A", color: "white" }} data-testid={`p2p-take-${o.id}`}>
                        Je reçois cette commande
                      </button>
                    )}
                    {mine && (
                      <button onClick={() => open(o.id)} className="btn btn-sm" style={{ background: "#0F172A", color: "white" }}>
                        Voir
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Mine view */}
      {tab !== "htg" && view === "mine" && (
        <div data-testid="p2p-mine-list">
          {myOrders.length === 0 && <div style={{ textAlign: "center", padding: "2rem 1rem", color: "#94A3B8" }}>Aucune transaction</div>}
          {myOrders.map(o => (
            <div key={o.id} className="card" style={{ marginBottom: 6, padding: "0.55rem 0.75rem", borderLeft: `4px solid ${STATUS_COLOR[o.status]}`, cursor: "pointer" }}
              onClick={() => open(o.id)} data-testid={`p2p-mine-${o.id}`}>
              <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 4 }}>
                <div>
                  <span style={{ fontSize: "0.65rem", padding: "1px 6px", borderRadius: 999, background: "#F1F5F9", color: "#64748B", fontWeight: 700, marginRight: 6, textTransform: "uppercase" }}>{o.kind}</span>
                  <span style={{ fontWeight: 800 }}>{fmt2(o.amount_usd)} $</span>
                  <span style={{ marginLeft: 8, fontSize: "0.72rem", color: STATUS_COLOR[o.status], fontWeight: 700 }}>{STATUS_LABEL[o.status] || o.status}</span>
                </div>
                <div style={{ fontSize: "0.7rem", color: "#94A3B8" }}>{(o.created_at || "").slice(0, 16).replace("T", " ")}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Offers view (Annonces marketplace) */}
      {tab !== "htg" && view === "offers" && (
        <div data-testid="p2p-offers-list">
          {/* Filters + Create button */}
          <div style={{ display: "flex", gap: 6, marginBottom: 8, flexWrap: "wrap", alignItems: "center" }}>
            <button onClick={() => { setEditOffer(null); setOfferKind(tab); setShowOfferCreate(true); }}
              className="btn btn-sm" data-testid="p2p-offer-create-btn"
              style={{ background: "#0EA5E9", color: "white", fontWeight: 800, fontSize: "0.78rem" }}>
              <Plus size={13} /> Publier une annonce
            </button>
            <select value={offerFilterDirection} onChange={(e) => setOfferFilterDirection(e.target.value)}
              className="form-input" data-testid="p2p-offer-filter-direction"
              style={{ flex: "0 0 auto", padding: "4px 8px", fontSize: "0.78rem", height: 30 }}>
              <option value="">Tous sens</option>
              <option value="sell">📤 Vente USD</option>
              <option value="buy">📥 Achat USD</option>
            </select>
            <input type="text" placeholder="Recherche par nom..."
              value={offerFilterQuery} onChange={(e) => setOfferFilterQuery(e.target.value)}
              className="form-input" data-testid="p2p-offer-filter-q"
              style={{ flex: 1, fontSize: "0.78rem", height: 30 }} />
          </div>

          {/* Mes annonces (résumé) */}
          {myOffers.filter(o => ["open", "claimed"].includes(o.status)).length > 0 && (
            <div style={{ background: "#FEFCE8", border: "1px solid #FDE68A", borderRadius: 10, padding: "0.5rem 0.7rem", marginBottom: 8 }}>
              <div style={{ fontSize: "0.7rem", fontWeight: 800, color: "#92400E", marginBottom: 4 }}>📌 MES ANNONCES ACTIVES</div>
              {myOffers.filter(o => ["open", "claimed"].includes(o.status)).map(o => (
                <div key={o.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.3rem 0", fontSize: "0.78rem", borderBottom: "1px dashed #FDE68A" }} data-testid={`p2p-my-offer-${o.id}`}>
                  <div>
                    <b>{o.kind.toUpperCase()}</b> {o.direction === "sell" ? "📤" : "📥"} <b>{o.amount_usd} $</b> @ {o.rate}
                    <span style={{ marginLeft: 6, padding: "1px 6px", borderRadius: 999, background: o.status === "open" ? "#DBEAFE" : "#FEF3C7", color: o.status === "open" ? "#1E40AF" : "#92400E", fontWeight: 700, fontSize: "0.65rem" }}>{o.status === "open" ? "Ouverte" : "Réservée"}</span>
                    <div style={{ fontSize: "0.65rem", color: "#92400E" }}>Caution {o.caution_htg} G {o.availability && `· ${o.availability}`}</div>
                  </div>
                  <div style={{ display: "flex", gap: 4 }}>
                    {o.status === "claimed" && (
                      <button onClick={() => handleFulfillOffer(o.id)} className="btn btn-sm" data-testid={`p2p-offer-fulfill-${o.id}`}
                        style={{ background: "#16A34A", color: "white", fontSize: "0.7rem", fontWeight: 700 }}>
                        ✓ Honorée
                      </button>
                    )}
                    {o.status === "open" && (
                      <>
                        <button onClick={() => { setEditOffer(o); setOfferKind(o.kind); setOfferDirection(o.direction); setOfferAmount(String(o.amount_usd)); setOfferRate(String(o.rate)); setOfferAvailability(o.availability || ""); setShowOfferCreate(true); }}
                          className="btn btn-sm" data-testid={`p2p-offer-edit-${o.id}`}
                          style={{ background: "#0EA5E9", color: "white", fontSize: "0.7rem", fontWeight: 700 }}>
                          ✎
                        </button>
                        <button onClick={() => handleDeleteOffer(o.id)} className="btn btn-sm" data-testid={`p2p-offer-delete-${o.id}`}
                          style={{ background: "#DC2626", color: "white", fontSize: "0.7rem", fontWeight: 700 }}>
                          ✗
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Public offers list */}
          {offers.length === 0 && (
            <div style={{ textAlign: "center", padding: "2rem 1rem", color: "#94A3B8", border: "1px dashed #E2E8F0", borderRadius: 10 }}>
              Aucune annonce {tab.toUpperCase()} active. Soyez le premier à publier !
            </div>
          )}
          {offers.map(o => {
            const mine = clientUser && o.publisher_id === clientUser.id;
            const isSell = o.direction === "sell";
            return (
              <div key={o.id} className="card" style={{ padding: "0.6rem 0.8rem", marginBottom: 8, borderLeft: `4px solid ${isSell ? "#16A34A" : "#3B82F6"}` }} data-testid={`p2p-offer-${o.id}`}>
                <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ fontWeight: 800, fontSize: "0.95rem" }}>
                      {o.publisher_name}
                      <span style={{ marginLeft: 6, padding: "1px 8px", borderRadius: 999, background: isSell ? "#D1FAE5" : "#DBEAFE", color: isSell ? "#065F46" : "#1E40AF", fontWeight: 700, fontSize: "0.65rem" }}>
                        {isSell ? "📤 VEND USD" : "📥 ACHÈTE USD"}
                      </span>
                    </div>
                    <div style={{ fontSize: "0.85rem", color: "#0F172A", marginTop: 2 }}>
                      <b>{o.kind.toUpperCase()}</b> · <b style={{ color: "#16A34A" }}>{o.amount_usd} $</b> @ <b>{o.rate} G/USD</b>
                      <span style={{ marginLeft: 6, color: "#94A3B8" }}>(≈ {fmt2(o.amount_usd * o.rate)} G)</span>
                    </div>
                    {o.availability && <div style={{ fontSize: "0.72rem", color: "#475569" }}>⏱ {o.availability}</div>}
                    <div style={{ fontSize: "0.65rem", color: "#94A3B8", marginTop: 2 }}>
                      Caution {o.caution_htg} G · {(o.created_at || "").slice(0, 16).replace("T", " ")}
                    </div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {mine ? (
                      <span style={{ fontSize: "0.7rem", color: "#94A3B8", fontStyle: "italic", padding: "4px 8px" }}>Votre annonce</span>
                    ) : (
                      <button onClick={() => handleClaimOffer(o)} className="btn btn-sm" data-testid={`p2p-offer-claim-${o.id}`}
                        style={{ background: "#0EA5E9", color: "white", fontWeight: 800, fontSize: "0.8rem" }}>
                        🛒 Commander
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Detail view */}
      {view === "detail" && order && (
        <OrderDetail
          order={order} isCreator={isCreator} isClaimer={isClaimer} clientUser={clientUser}
          tab={tab} exchangeRate={exchangeRate}
          onBack={() => { setView("list"); setOrder(null); setSelectedId(null); }}
          showClaim={showClaim} setShowClaim={setShowClaim}
          claimRate={claimRate} setClaimRate={setClaimRate}
          claimFee={claimFee} setClaimFee={setClaimFee}
          handleClaim={handleClaim}
          handleRespond={handleRespond}
          showInfo={showInfo} setShowInfo={setShowInfo}
          infoEmail={infoEmail} setInfoEmail={setInfoEmail}
          infoFirst={infoFirst} setInfoFirst={setInfoFirst}
          infoLast={infoLast} setInfoLast={setInfoLast}
          infoTag={infoTag} setInfoTag={setInfoTag}
          handleSubmitInfo={handleSubmitInfo}
          proofUrl={proofUrl} handleProofFile={handleProofFile} uploadingProof={uploadingProof}
          handleSubmitProof={handleSubmitProof}
          handleValidate={handleValidate} handleCancel={handleCancel}
          remainingMin={remainingMin}
          reviewStars={reviewStars} setReviewStars={setReviewStars}
          reviewComment={reviewComment} setReviewComment={setReviewComment}
          handleReview={handleReview}
        />
      )}

      {/* Create modal */}
      {showCreate && (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowCreate(false); }}>
          <div className="modal-content" data-testid="p2p-create-modal" style={{ maxWidth: 420 }}>
            <h3 style={{ marginTop: 0 }}>Créer une commande {tab.toUpperCase()}</h3>
            <p style={{ fontSize: "0.78rem", color: "#475569" }}>
              {tab === "zelle"
                ? "Vous recevrez les USD via Zelle d'un autre client en échange de gourdes débitées de son solde."
                : "Vous enverrez des gourdes via votre solde en échange de USD via Wise. Solde HTG requis."}
            </p>
            <label style={{ fontSize: "0.78rem", fontWeight: 700, display: "block", marginBottom: 4 }}>Montant en USD</label>
            <input type="number" min="5" step="0.01" className="form-input" placeholder="Ex: 100"
              value={createAmount} onChange={(e) => setCreateAmount(e.target.value)} data-testid="p2p-create-amount" />
            <div style={{ display: "flex", gap: 6, marginTop: 12, justifyContent: "flex-end" }}>
              <button className="btn btn-sm" onClick={() => setShowCreate(false)} style={{ background: "#E2E8F0" }}>Annuler</button>
              <button className="btn btn-sm btn-primary" onClick={handleCreate} data-testid="p2p-create-submit">Commander</button>
            </div>
          </div>
        </div>
      )}

      {/* Recharge P2P Balance modal */}
      {showRecharge && (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowRecharge(false); }}>
          <div className="modal-content" data-testid="p2p-recharge-modal" style={{ maxWidth: 520, maxHeight: "92vh", overflowY: "auto" }}>
            <h3 style={{ marginTop: 0, color: "#7C3AED" }}>💳 Recharger mon Solde P2P</h3>
            <p style={{ fontSize: "0.78rem", color: "#475569", marginBottom: 8 }}>
              Choisissez la méthode, payez à l'admin, puis téléversez la preuve.
              L'admin vérifiera votre transaction avant de créditer.
            </p>

            {/* Method selection */}
            <label style={{ fontSize: "0.78rem", fontWeight: 700, display: "block", marginBottom: 4 }}>1. Méthode de paiement</label>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 6, marginBottom: 10 }} data-testid="p2p-recharge-methods">
              {rechargeMethods.map(m => {
                const selected = rechargeMethodKey === m.key;
                return (
                  <button key={m.key}
                    onClick={() => setRechargeMethodKey(m.key)}
                    className="btn btn-sm"
                    data-testid={`p2p-recharge-method-${m.key}`}
                    style={{
                      background: selected ? "#7C3AED" : "#F1F5F9",
                      color: selected ? "white" : "#0F172A",
                      fontWeight: 700, fontSize: "0.78rem",
                      flexDirection: "column", alignItems: "flex-start",
                      padding: "0.5rem", textAlign: "left",
                    }}>
                    <span>{m.label}</span>
                    <span style={{ fontSize: "0.65rem", opacity: 0.85, marginTop: 2 }}>
                      → crédit en {m.currency}
                    </span>
                  </button>
                );
              })}
              {rechargeMethods.length === 0 && (
                <span style={{ fontSize: "0.74rem", color: "#94A3B8" }}>Chargement…</span>
              )}
            </div>

            {/* Selected method info */}
            {rechargeMethodKey && (() => {
              const m = rechargeMethods.find(x => x.key === rechargeMethodKey);
              if (!m) return null;
              return (
                <div style={{ background: "#F5F3FF", border: "1px solid #DDD6FE", borderRadius: 8, padding: "0.55rem 0.7rem", marginBottom: 10, fontSize: "0.78rem" }}
                  data-testid="p2p-recharge-method-info">
                  <div style={{ fontWeight: 800, color: "#5B21B6", marginBottom: 4 }}>
                    Envoyez votre paiement à :
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                    <span style={{ fontFamily: "monospace", fontSize: "0.85rem", fontWeight: 800, color: "#1E1B4B", wordBreak: "break-all" }}
                      data-testid="p2p-recharge-method-value">{m.value}</span>
                    <button className="btn btn-sm" style={{ background: "#7C3AED", color: "white", fontSize: "0.7rem", padding: "2px 8px" }}
                      onClick={() => { navigator.clipboard?.writeText(m.value); toast.success("Copié"); }}
                      data-testid="p2p-recharge-method-copy">
                      Copier
                    </button>
                  </div>
                  <div style={{ fontSize: "0.7rem", color: "#5B21B6", marginTop: 4 }}>
                    Vous serez crédité dans la devise <b>{m.currency}</b>
                    {m.currency === "USD" ? " (équivalent HTG converti automatiquement)." : "."}
                  </div>
                </div>
              );
            })()}

            {/* Amount */}
            <label style={{ fontSize: "0.78rem", fontWeight: 700, display: "block", marginBottom: 4 }}>
              2. Montant envoyé {rechargeMethodKey && (() => {
                const m = rechargeMethods.find(x => x.key === rechargeMethodKey);
                return m ? `(en ${m.currency})` : "";
              })()}
            </label>
            <input type="number" min="0" step="0.01" className="form-input" placeholder="Ex: 250"
              value={rechargeAmount} onChange={(e) => setRechargeAmount(e.target.value)} data-testid="p2p-recharge-amount" />

            {/* Note */}
            <label style={{ fontSize: "0.78rem", fontWeight: 700, display: "block", marginBottom: 4, marginTop: 10 }}>
              3. Note (optionnel)
            </label>
            <input type="text" className="form-input" placeholder="Référence ou commentaire"
              value={rechargeNote} onChange={(e) => setRechargeNote(e.target.value)} data-testid="p2p-recharge-note" />

            {/* Proof upload */}
            <label style={{ fontSize: "0.78rem", fontWeight: 700, display: "block", marginBottom: 4, marginTop: 10 }}>
              4. Preuve de paiement (capture d'écran)
            </label>
            <input type="file" accept="image/*" onChange={handleRechargeFile} data-testid="p2p-recharge-proof-input"
              style={{ width: "100%", fontSize: "0.78rem" }} />
            {rechargeUploading && <div style={{ fontSize: "0.72rem", color: "#7C3AED", marginTop: 4 }}>Lecture du fichier…</div>}
            {rechargeProof && (
              <div style={{ marginTop: 6 }} data-testid="p2p-recharge-proof-preview">
                <img src={rechargeProof} alt="preuve" style={{ maxWidth: "100%", maxHeight: 160, borderRadius: 6, border: "1px solid #E2E8F0" }} />
              </div>
            )}

            <div style={{ background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 8, padding: "0.5rem 0.7rem", marginTop: 10, fontSize: "0.7rem", color: "#92400E" }}>
              <AlertTriangle size={11} /> <b>L'IA filtre les images non-paiement.</b> Selfies, captures aléatoires ou photos sans rapport seront automatiquement refusées.
            </div>

            <div style={{ display: "flex", gap: 6, marginTop: 14, justifyContent: "flex-end" }}>
              <button className="btn btn-sm" onClick={() => setShowRecharge(false)} style={{ background: "#E2E8F0" }} data-testid="p2p-recharge-cancel">Annuler</button>
              <button className="btn btn-sm" onClick={handleSubmitRecharge}
                disabled={rechargeSubmitting || !rechargeProof || !rechargeMethodKey || !rechargeAmount}
                style={{ background: "#7C3AED", color: "white", fontWeight: 800, opacity: (rechargeSubmitting || !rechargeProof || !rechargeMethodKey || !rechargeAmount) ? 0.6 : 1 }}
                data-testid="p2p-recharge-submit">
                {rechargeSubmitting ? "Envoi…" : "Envoyer la demande"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Offer Create/Edit modal */}
      {showOfferCreate && (() => {
        const amt = parseFloat(offerAmount) || 0;
        const rt = parseFloat(offerRate) || 0;
        const caution = (amt * rt * 0.05).toFixed(2);
        const isEdit = !!editOffer;
        return (
          <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) { setShowOfferCreate(false); setEditOffer(null); } }}>
            <div className="modal-content" data-testid="p2p-offer-modal" style={{ maxWidth: 480, maxHeight: "92vh", overflowY: "auto" }}>
              <h3 style={{ marginTop: 0, color: "#0EA5E9" }}>📢 {isEdit ? "Modifier l'annonce" : "Publier une annonce P2P"}</h3>
              {!isEdit && (
                <>
                  <label style={{ fontSize: "0.78rem", fontWeight: 700, display: "block", marginBottom: 4 }}>1. Plateforme</label>
                  <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                    {["wise", "zelle"].map(k => (
                      <button key={k} type="button" onClick={() => setOfferKind(k)}
                        className="btn btn-sm" data-testid={`p2p-offer-kind-${k}`}
                        style={{ flex: 1, background: offerKind === k ? "#0F172A" : "#F1F5F9", color: offerKind === k ? "white" : "#0F172A", fontWeight: 700, textTransform: "uppercase" }}>
                        {k}
                      </button>
                    ))}
                  </div>
                  <label style={{ fontSize: "0.78rem", fontWeight: 700, display: "block", marginBottom: 4 }}>2. Sens de l'annonce</label>
                  <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                    <button type="button" onClick={() => setOfferDirection("sell")}
                      className="btn btn-sm" data-testid="p2p-offer-direction-sell"
                      style={{ flex: 1, background: offerDirection === "sell" ? "#16A34A" : "#F1F5F9", color: offerDirection === "sell" ? "white" : "#0F172A", fontWeight: 700, fontSize: "0.78rem" }}>
                      📤 J'ai des USD (je vends)
                    </button>
                    <button type="button" onClick={() => setOfferDirection("buy")}
                      className="btn btn-sm" data-testid="p2p-offer-direction-buy"
                      style={{ flex: 1, background: offerDirection === "buy" ? "#3B82F6" : "#F1F5F9", color: offerDirection === "buy" ? "white" : "#0F172A", fontWeight: 700, fontSize: "0.78rem" }}>
                      📥 Je veux des USD (j'achète)
                    </button>
                  </div>
                </>
              )}

              <label style={{ fontSize: "0.78rem", fontWeight: 700, display: "block", marginBottom: 4 }}>{isEdit ? "" : "3. "}Montant (USD)</label>
              <input type="number" min="5" step="0.01" className="form-input" placeholder="Ex: 50"
                value={offerAmount} onChange={(e) => setOfferAmount(e.target.value)} data-testid="p2p-offer-amount" />

              <label style={{ fontSize: "0.78rem", fontWeight: 700, display: "block", marginBottom: 4, marginTop: 8 }}>{isEdit ? "" : "4. "}Taux (HTG par USD)</label>
              <input type="number" min="50" max="500" step="0.01" className="form-input" placeholder="Ex: 147"
                value={offerRate} onChange={(e) => setOfferRate(e.target.value)} data-testid="p2p-offer-rate" />

              <label style={{ fontSize: "0.78rem", fontWeight: 700, display: "block", marginBottom: 4, marginTop: 8 }}>{isEdit ? "" : "5. "}Disponibilité</label>
              <input type="text" className="form-input" placeholder="Ex: Immédiat / Après 18h / Demain matin"
                value={offerAvailability} onChange={(e) => setOfferAvailability(e.target.value)} data-testid="p2p-offer-availability" />

              <div style={{ background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 8, padding: "0.5rem 0.7rem", marginTop: 10, fontSize: "0.74rem", color: "#92400E" }}>
                💰 <b>Caution 5% (HTG) : {caution} G</b> sera verrouillée sur votre solde P2P HTG.
                Elle est <b>remboursée intégralement</b> si vous annulez l'annonce ou honorez la transaction.
                Elle est <b>perdue</b> si vous ne répondez pas dans les 50 min après commande.
              </div>

              <div style={{ display: "flex", gap: 6, marginTop: 14, justifyContent: "flex-end" }}>
                <button className="btn btn-sm" onClick={() => { setShowOfferCreate(false); setEditOffer(null); }} style={{ background: "#E2E8F0" }}>Annuler</button>
                <button className="btn btn-sm" onClick={handleCreateOffer}
                  disabled={offerSubmitting || !amt || amt < 5 || !rt}
                  style={{ background: "#0EA5E9", color: "white", fontWeight: 800, opacity: (offerSubmitting || !amt || amt < 5 || !rt) ? 0.6 : 1 }}
                  data-testid="p2p-offer-submit">
                  {offerSubmitting ? "Envoi…" : (isEdit ? "Mettre à jour" : "Publier")}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Convert P2P modal (HTG ↔ USD) */}
      {showConvert && (() => {
        const amt = parseFloat(convertAmount) || 0;
        const rate = convertFrom === "HTG" ? convertRates.htg_to_usd_rate : convertRates.usd_to_htg_rate;
        const result = convertFrom === "HTG"
          ? Math.floor((amt / (rate || 1)) * 100) / 100
          : Math.floor((amt * (rate || 1)) * 100) / 100;
        const fromSuf = convertFrom === "USD" ? "$" : "G";
        const toSuf = convertFrom === "USD" ? "G" : "$";
        const maxAvail = convertFrom === "HTG" ? p2pHtg : p2pUsd;
        const insufficient = amt > maxAvail + 0.01;
        return (
          <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowConvert(false); }}>
            <div className="modal-content" data-testid="p2p-convert-modal" style={{ maxWidth: 460 }}>
              <h3 style={{ marginTop: 0, color: "#0EA5E9" }}>⇄ Convertir Solde P2P</h3>
              <p style={{ fontSize: "0.78rem", color: "#475569", marginBottom: 8 }}>
                Convertissez entre vos deux soldes au taux affiché. La marge d'arrondi est conservée par la plateforme.
              </p>

              {/* Direction toggle */}
              <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
                <button type="button" onClick={() => setConvertFrom("HTG")}
                  className="btn btn-sm" data-testid="p2p-convert-direction-htg-usd"
                  style={{ flex: 1, background: convertFrom === "HTG" ? "#0EA5E9" : "#F1F5F9", color: convertFrom === "HTG" ? "white" : "#0F172A", fontWeight: 700 }}>
                  HTG → USD
                </button>
                <button type="button" onClick={() => setConvertFrom("USD")}
                  className="btn btn-sm" data-testid="p2p-convert-direction-usd-htg"
                  style={{ flex: 1, background: convertFrom === "USD" ? "#0EA5E9" : "#F1F5F9", color: convertFrom === "USD" ? "white" : "#0F172A", fontWeight: 700 }}>
                  USD → HTG
                </button>
              </div>

              <div style={{ background: "#F0F9FF", border: "1px solid #BAE6FD", borderRadius: 8, padding: "0.55rem 0.7rem", marginBottom: 10, fontSize: "0.78rem" }}>
                Taux actuel : <b>{convertFrom === "HTG" ? `1 USD = ${rate} G` : `1 USD = ${rate} G`}</b><br/>
                Disponible : <b>{Number(maxAvail).toLocaleString()} {fromSuf}</b>
              </div>

              <label style={{ fontSize: "0.78rem", fontWeight: 700, display: "block", marginBottom: 4 }}>
                Montant à convertir ({fromSuf})
              </label>
              <input type="number" min="0" step="0.01" className="form-input" placeholder={convertFrom === "HTG" ? "Ex: 1500" : "Ex: 10"}
                value={convertAmount} onChange={(e) => setConvertAmount(e.target.value)} data-testid="p2p-convert-amount" />

              {amt > 0 && (
                <div style={{ background: insufficient ? "#FEE2E2" : "#D1FAE5", border: `1px solid ${insufficient ? "#FCA5A5" : "#86EFAC"}`, borderRadius: 8, padding: "0.5rem 0.7rem", marginTop: 10, fontSize: "0.85rem", fontWeight: 700, color: insufficient ? "#991B1B" : "#065F46" }} data-testid="p2p-convert-preview">
                  {insufficient ? `Solde insuffisant (max ${Number(maxAvail).toFixed(2)} ${fromSuf})` : `Vous recevrez : ${result.toFixed(2)} ${toSuf}`}
                </div>
              )}

              <div style={{ display: "flex", gap: 6, marginTop: 14, justifyContent: "flex-end" }}>
                <button className="btn btn-sm" onClick={() => setShowConvert(false)} style={{ background: "#E2E8F0" }}>Annuler</button>
                <button className="btn btn-sm" onClick={handleSubmitConvert}
                  disabled={convertSubmitting || !amt || insufficient}
                  style={{ background: "#0EA5E9", color: "white", fontWeight: 800, opacity: (convertSubmitting || !amt || insufficient) ? 0.6 : 1 }}
                  data-testid="p2p-convert-submit">
                  {convertSubmitting ? "Conversion…" : "Convertir"}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Withdraw P2P modal */}
      {showWithdraw && (() => {
        const methods = withdrawMethods[withdrawCurrency] || [];
        const meta = methods.find(m => m.key === withdrawMethod);
        const maxAvail = withdrawCurrency === "HTG" ? p2pHtg : p2pUsd;
        const amt = parseFloat(withdrawAmount) || 0;
        const insufficient = amt > maxAvail + 0.01;
        const sfx = withdrawCurrency === "USD" ? "$" : "G";
        return (
          <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowWithdraw(false); }}>
            <div className="modal-content" data-testid="p2p-withdraw-modal" style={{ maxWidth: 480, maxHeight: "92vh", overflowY: "auto" }}>
              <h3 style={{ marginTop: 0, color: "#DC2626" }}>↗ Retirer mon Solde P2P</h3>

              {/* Currency toggle */}
              <label style={{ fontSize: "0.78rem", fontWeight: 700, display: "block", marginBottom: 4 }}>1. Devise à retirer</label>
              <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
                <button type="button" onClick={() => { setWithdrawCurrency("HTG"); setWithdrawMethod(""); }}
                  className="btn btn-sm" data-testid="p2p-withdraw-currency-htg"
                  style={{ flex: 1, background: withdrawCurrency === "HTG" ? "#F59E0B" : "#F1F5F9", color: withdrawCurrency === "HTG" ? "white" : "#0F172A", fontWeight: 700 }}>
                  Gourdes (G) — {Number(p2pHtg).toLocaleString()} G
                </button>
                <button type="button" onClick={() => { setWithdrawCurrency("USD"); setWithdrawMethod(""); }}
                  className="btn btn-sm" data-testid="p2p-withdraw-currency-usd"
                  style={{ flex: 1, background: withdrawCurrency === "USD" ? "#16A34A" : "#F1F5F9", color: withdrawCurrency === "USD" ? "white" : "#0F172A", fontWeight: 700 }}>
                  Dollars ($) — {Number(p2pUsd).toFixed(2)} $
                </button>
              </div>

              {/* Method */}
              <label style={{ fontSize: "0.78rem", fontWeight: 700, display: "block", marginBottom: 4 }}>2. Méthode de retrait</label>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 6, marginBottom: 10 }}>
                {methods.map(m => (
                  <button key={m.key} type="button" onClick={() => setWithdrawMethod(m.key)}
                    className="btn btn-sm" data-testid={`p2p-withdraw-method-${m.key}`}
                    style={{ background: withdrawMethod === m.key ? "#DC2626" : "#F1F5F9", color: withdrawMethod === m.key ? "white" : "#0F172A", fontWeight: 700, fontSize: "0.78rem" }}>
                    {m.label}
                  </button>
                ))}
              </div>

              {/* Amount */}
              <label style={{ fontSize: "0.78rem", fontWeight: 700, display: "block", marginBottom: 4 }}>3. Montant ({sfx})</label>
              <input type="number" min="0" step="0.01" className="form-input"
                placeholder={withdrawCurrency === "USD" ? "Ex: 10" : "Ex: 5000"}
                value={withdrawAmount} onChange={(e) => setWithdrawAmount(e.target.value)} data-testid="p2p-withdraw-amount" />
              {amt > 0 && insufficient && (
                <div style={{ fontSize: "0.7rem", color: "#DC2626", fontWeight: 700, marginTop: 4 }}>
                  Solde insuffisant (max {Number(maxAvail).toFixed(2)} {sfx})
                </div>
              )}

              {/* Account */}
              {meta && meta.needs_account && (
                <>
                  <label style={{ fontSize: "0.78rem", fontWeight: 700, display: "block", marginBottom: 4, marginTop: 10 }}>4. {meta.account_label}</label>
                  <input type="text" className="form-input"
                    placeholder={meta.account_label}
                    value={withdrawAccount} onChange={(e) => setWithdrawAccount(e.target.value)} data-testid="p2p-withdraw-account" />
                </>
              )}

              {/* Note */}
              <label style={{ fontSize: "0.78rem", fontWeight: 700, display: "block", marginBottom: 4, marginTop: 10 }}>5. Note (optionnel)</label>
              <input type="text" className="form-input"
                placeholder="Référence ou commentaire"
                value={withdrawNote} onChange={(e) => setWithdrawNote(e.target.value)} data-testid="p2p-withdraw-note" />

              <div style={{ background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 8, padding: "0.5rem 0.7rem", marginTop: 10, fontSize: "0.7rem", color: "#92400E" }}>
                ⚠ Le montant sera <b>verrouillé immédiatement</b>. Après soumission, vous serez redirigé vers WhatsApp pour finaliser avec l'admin.
                Si l'admin refuse, le montant sera remboursé automatiquement.
              </div>

              <div style={{ display: "flex", gap: 6, marginTop: 14, justifyContent: "flex-end" }}>
                <button className="btn btn-sm" onClick={() => setShowWithdraw(false)} style={{ background: "#E2E8F0" }}>Annuler</button>
                <button className="btn btn-sm" onClick={handleSubmitWithdraw}
                  disabled={withdrawSubmitting || !amt || insufficient || !withdrawMethod}
                  style={{ background: "#DC2626", color: "white", fontWeight: 800, opacity: (withdrawSubmitting || !amt || insufficient || !withdrawMethod) ? 0.6 : 1 }}
                  data-testid="p2p-withdraw-submit">
                  {withdrawSubmitting ? "Envoi…" : "Demander le retrait"}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

    </div>
  );
}

function StarRating({ value, onChange, readOnly = false }) {
  return (
    <div style={{ display: "inline-flex", gap: 2 }}>
      {[1, 2, 3, 4, 5].map(i => (
        <Star
          key={i}
          size={18}
          fill={i <= value ? "#FCD34D" : "transparent"}
          color={i <= value ? "#FCD34D" : "#CBD5E1"}
          style={{ cursor: readOnly ? "default" : "pointer" }}
          onClick={() => !readOnly && onChange?.(i)}
        />
      ))}
    </div>
  );
}

function OrderDetail(props) {
  const {
    order, isCreator, isClaimer, tab, onBack, exchangeRate,
    showClaim, setShowClaim, claimRate, setClaimRate, claimFee, setClaimFee, handleClaim,
    handleRespond, showInfo, setShowInfo, infoEmail, setInfoEmail, infoFirst, setInfoFirst,
    infoLast, setInfoLast, infoTag, setInfoTag, handleSubmitInfo,
    proofUrl, handleProofFile, uploadingProof, handleSubmitProof,
    handleValidate, handleCancel, remainingMin,
    reviewStars, setReviewStars, reviewComment, setReviewComment, handleReview,
  } = props;
  const [stats, setStats] = useState(null);
  const counterpartyId = isCreator ? order.claimer_id : (isClaimer ? order.creator_id : null);
  useEffect(() => {
    if (!counterpartyId) return;
    axios.get(`${API}/p2p/stats/${counterpartyId}`, { headers: auth() })
      .then(r => setStats(r.data)).catch(() => { });
  }, [counterpartyId]);

  const rem = remainingMin(order);
  const baseHtg = order.escrow_amount_htg || (order.amount_usd * (order.claim_rate || 0));
  const isZelle = order.kind === "zelle";

  // Visibility of action buttons per status + role
  const status = order.status;
  return (
    <div className="card" data-testid={`p2p-detail-${order.id}`}>
      <button onClick={onBack} className="btn btn-sm" style={{ background: "#E2E8F0", marginBottom: 8 }}>
        <ArrowLeft size={13} /> Retour à la liste
      </button>
      <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
        <div>
          <div style={{ fontSize: "0.7rem", color: "#94A3B8", textTransform: "uppercase", fontWeight: 700 }}>Commande {order.kind}</div>
          <div style={{ fontSize: "1.4rem", fontWeight: 800 }}>{fmt2(order.amount_usd)} $</div>
          <div style={{ fontSize: "0.78rem", color: STATUS_COLOR[status], fontWeight: 700 }}>{STATUS_LABEL[status] || status}</div>
        </div>
        <div style={{ textAlign: "right" }}>
          {rem !== null && rem > 0 && status !== "validated" && status !== "cancelled" && (
            <div style={{ fontSize: "0.85rem", color: rem < 10 ? "#DC2626" : "#0F172A", fontWeight: 700 }}>
              <Clock size={13} style={{ verticalAlign: -2 }} /> {rem} min restantes
            </div>
          )}
          {rem === 0 && status !== "validated" && status !== "cancelled" && (
            <div style={{ fontSize: "0.78rem", color: "#DC2626", fontWeight: 700 }}>
              <AlertTriangle size={13} style={{ verticalAlign: -2 }} /> Délai expiré — Admin doit intervenir
            </div>
          )}
        </div>
      </div>

      {/* Counterparty card */}
      {counterpartyId && (
        <div style={{ background: "#F8FAFC", padding: "0.6rem", borderRadius: 8, marginBottom: 8 }}>
          <div style={{ fontSize: "0.7rem", color: "#94A3B8", marginBottom: 2, fontWeight: 700, textTransform: "uppercase" }}>Contrepartie</div>
          <div style={{ fontWeight: 700 }}>{isCreator ? order.claimer_name : order.creator_name}</div>
          {stats && (
            <div style={{ fontSize: "0.72rem", color: "#475569", display: "flex", gap: 8, marginTop: 3, flexWrap: "wrap" }}>
              <span><Award size={11} style={{ verticalAlign: -2 }} /> {stats.trust_level}</span>
              <span><Users size={11} style={{ verticalAlign: -2 }} /> {stats.served} servis</span>
              <span><Star size={11} fill="#FCD34D" color="#FCD34D" style={{ verticalAlign: -2 }} /> {stats.avg_stars} ({stats.reviews_count})</span>
            </div>
          )}
        </div>
      )}

      {/* Negotiation summary */}
      {order.claim_rate > 0 && (
        <div style={{ background: "#EFF6FF", padding: "0.5rem 0.7rem", borderRadius: 8, marginBottom: 8, fontSize: "0.78rem" }}>
          <div>Taux : <strong>{fmt2(order.claim_rate)}</strong> HTG/USD</div>
          {isZelle && order.claim_fee_pct > 0 && <div>Frais receveur : <strong>{order.claim_fee_pct}%</strong></div>}
          <div>Montant en escrow : <strong>{fmt2(baseHtg)} G</strong> ({formatUSD(baseHtg, exchangeRate)})</div>
        </div>
      )}

      {/* Action panels per status */}
      {/* OPEN: creator can cancel */}
      {status === "open" && isCreator && (
        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={handleCancel} className="btn btn-sm" style={{ background: "#FEE2E2", color: "#B91C1C", flex: 1 }} data-testid="p2p-cancel-btn">
            <X size={13} /> Annuler ma commande
          </button>
        </div>
      )}

      {/* CLAIMED: creator decides accept/refuse */}
      {status === "claimed" && isCreator && (
        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={() => handleRespond("accept")} className="btn btn-sm" style={{ background: "#16A34A", color: "white", flex: 1 }} data-testid="p2p-accept-btn">
            <CheckCircle size={13} /> Accepter
          </button>
          <button onClick={() => handleRespond("refuse")} className="btn btn-sm" style={{ background: "#FEE2E2", color: "#B91C1C", flex: 1 }} data-testid="p2p-refuse-btn">
            <X size={13} /> Refuser
          </button>
        </div>
      )}

      {/* ACCEPTED_ZELLE: claimer (B) provides Zelle info */}
      {status === "accepted_zelle" && isClaimer && (
        <button onClick={() => setShowInfo(true)} className="btn btn-sm btn-primary" style={{ width: "100%" }} data-testid="p2p-provide-info-btn">
          <Send size={13} /> Fournir mes infos Zelle
        </button>
      )}

      {/* ACCEPTED_WISE: creator (A) provides Wise info AND locks HTG */}
      {status === "accepted_wise" && isCreator && (
        <button onClick={() => setShowInfo(true)} className="btn btn-sm btn-primary" style={{ width: "100%" }} data-testid="p2p-provide-info-btn">
          <Send size={13} /> Fournir mes infos Wise (verrouille mon solde)
        </button>
      )}

      {/* INFO_PROVIDED: counterparty uploads proof */}
      {status === "info_provided" && (
        <>
          {/* Show counterparty info */}
          <div style={{ background: "#F0FDF4", padding: "0.6rem 0.8rem", borderRadius: 8, marginBottom: 8, fontSize: "0.85rem", border: "1px solid #BBF7D0" }} data-testid="p2p-counterparty-info">
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Informations {isZelle ? "Zelle" : "Wise"} :</div>
            <div>📧 {isZelle ? order.zelle_email : order.wise_email}</div>
            <div>👤 {isZelle ? `${order.zelle_first_name} ${order.zelle_last_name}` : `${order.wise_first_name} ${order.wise_last_name}`}</div>
            {!isZelle && order.wise_tag && <div>🏷️ Tag: {order.wise_tag}</div>}
          </div>
          {/* Who uploads: Zelle → creator A; Wise → claimer B */}
          {((isZelle && isCreator) || (!isZelle && isClaimer)) && (
            <>
              <div style={{ border: "2px dashed #CBD5E1", borderRadius: 10, padding: "0.6rem", textAlign: "center", cursor: "pointer", background: proofUrl ? "#F0FDF4" : "#F8FAFC", marginBottom: 8 }}
                onClick={() => document.getElementById("p2p-proof-file").click()}>
                <input id="p2p-proof-file" type="file" accept="image/*" style={{ display: "none" }} onChange={handleProofFile} data-testid="p2p-proof-input" />
                {uploadingProof ? "Téléversement..." : proofUrl ? <span style={{ color: "#16A34A", fontWeight: 700 }}><CheckCircle size={14} /> Preuve attachée</span> : <span style={{ color: "#64748B" }}><Upload size={14} /> Joindre la preuve</span>}
              </div>
              <button onClick={handleSubmitProof} className="btn btn-sm btn-primary" style={{ width: "100%" }} data-testid="p2p-submit-proof-btn" disabled={!proofUrl}>
                Soumettre la preuve
              </button>
            </>
          )}
        </>
      )}

      {/* PROOF_UPLOADED: counterparty validates */}
      {status === "proof_uploaded" && (
        <>
          {order.proof_url && (
            <a href={order.proof_url} target="_blank" rel="noopener noreferrer" data-testid="p2p-view-proof">
              <img src={order.proof_url} alt="Preuve" style={{ maxWidth: "100%", borderRadius: 8, marginBottom: 8 }} />
            </a>
          )}
          {((isZelle && isClaimer) || (!isZelle && isCreator)) && (
            <button onClick={handleValidate} className="btn btn-sm" style={{ background: "#16A34A", color: "white", width: "100%" }} data-testid="p2p-validate-btn">
              <CheckCircle size={13} /> {isZelle ? "Valider — j'ai reçu les USD" : "J'ai reçu, merci"}
            </button>
          )}
        </>
      )}

      {/* VALIDATED: review */}
      {status === "validated" && counterpartyId && (
        <div style={{ background: "#FEF3C7", padding: "0.7rem", borderRadius: 8, marginTop: 8 }}>
          <div style={{ fontWeight: 700, marginBottom: 6, fontSize: "0.85rem" }}>Notez votre contrepartie</div>
          <StarRating value={reviewStars} onChange={setReviewStars} />
          <textarea className="form-input" rows={2} placeholder="Commentaire (optionnel)" value={reviewComment} onChange={(e) => setReviewComment(e.target.value)} style={{ marginTop: 6, fontSize: "0.85rem" }} data-testid="p2p-review-comment" />
          <button onClick={handleReview} className="btn btn-sm btn-primary" style={{ marginTop: 6 }} data-testid="p2p-review-submit">
            Envoyer la note
          </button>
        </div>
      )}

      {/* CANCELLED message */}
      {status === "cancelled" && (
        <div style={{ background: "#FEE2E2", color: "#991B1B", padding: "0.6rem", borderRadius: 8, fontSize: "0.85rem" }}>
          ❌ {order.cancelled_reason || "Commande annulée"}
        </div>
      )}

      {/* Claim modal */}
      {showClaim && (
        <ClaimModal {...{ tab, claimRate, setClaimRate, claimFee, setClaimFee, order, exchangeRate, onClose: () => setShowClaim(false), onSubmit: handleClaim }} />
      )}

      {/* Info modal */}
      {showInfo && (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowInfo(false); }}>
          <div className="modal-content" data-testid="p2p-info-modal" style={{ maxWidth: 420 }}>
            <h3 style={{ marginTop: 0 }}>Vos infos {isZelle ? "Zelle" : "Wise"}</h3>
            <input className="form-input" placeholder="Email" value={infoEmail} onChange={(e) => setInfoEmail(e.target.value)} style={{ marginBottom: 6 }} data-testid="p2p-info-email" />
            <input className="form-input" placeholder="Prénom" value={infoFirst} onChange={(e) => setInfoFirst(e.target.value)} style={{ marginBottom: 6 }} data-testid="p2p-info-first" />
            <input className="form-input" placeholder="Nom" value={infoLast} onChange={(e) => setInfoLast(e.target.value)} style={{ marginBottom: 6 }} data-testid="p2p-info-last" />
            {!isZelle && (
              <input className="form-input" placeholder="Wise Tag (optionnel)" value={infoTag} onChange={(e) => setInfoTag(e.target.value)} style={{ marginBottom: 6 }} data-testid="p2p-info-tag" />
            )}
            <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", marginTop: 8 }}>
              <button className="btn btn-sm" onClick={() => setShowInfo(false)} style={{ background: "#E2E8F0" }}>Annuler</button>
              <button className="btn btn-sm btn-primary" onClick={handleSubmitInfo} data-testid="p2p-info-submit">Informer le client</button>
            </div>
          </div>
        </div>
      )}

      {/* CLAIM trigger only when status=open and not creator */}
      {status === "open" && !isCreator && (
        <button onClick={() => setShowClaim(true)} className="btn btn-sm" style={{ background: "#16A34A", color: "white", width: "100%", marginTop: 8 }} data-testid="p2p-take-detail-btn">
          Je reçois cette commande
        </button>
      )}
    </div>
  );
}

function ClaimModal({ tab, claimRate, setClaimRate, claimFee, setClaimFee, order, exchangeRate, onClose, onSubmit }) {
  const baseHtg = (parseFloat(claimRate || 0)) * (order?.amount_usd || 0);
  const isZelle = tab === "zelle";
  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-content" data-testid="p2p-claim-modal" style={{ maxWidth: 460 }}>
        <h3 style={{ marginTop: 0 }}>Je reçois cette commande {tab.toUpperCase()}</h3>
        <p style={{ fontSize: "0.78rem", color: "#475569" }}>
          {isZelle
            ? "Vous recevrez les USD via Zelle. Vous payez le créateur en HTG (depuis votre solde)."
            : "Vous recevrez les HTG sur votre solde. Le créateur vous enverra les USD via Wise."}
        </p>
        <label style={{ fontSize: "0.78rem", fontWeight: 700 }}>Taux HTG / USD</label>
        <input type="number" min="1" step="0.01" className="form-input" placeholder="Ex: 130"
          value={claimRate} onChange={(e) => setClaimRate(e.target.value)} style={{ marginBottom: 6 }} data-testid="p2p-claim-rate" />
        {isZelle && (
          <>
            <label style={{ fontSize: "0.78rem", fontWeight: 700 }}>Vos frais (% — votre commission)</label>
            <input type="number" min="0" max="25" step="0.1" className="form-input" placeholder="Ex: 2"
              value={claimFee} onChange={(e) => setClaimFee(e.target.value)} style={{ marginBottom: 6 }} data-testid="p2p-claim-fee" />
          </>
        )}
        {baseHtg > 0 && (
          <div style={{ background: "#EFF6FF", padding: "0.5rem", borderRadius: 8, fontSize: "0.78rem", marginBottom: 8 }}>
            Montant à verrouiller depuis {isZelle ? "votre solde" : "le solde du créateur"} :
            <strong> {fmt2(baseHtg)} G</strong> ({formatUSD(baseHtg, exchangeRate)})
            <div style={{ fontSize: "0.7rem", color: "#64748B", marginTop: 2 }}>+ frais admin 1.5% = {fmt2(baseHtg * 0.015)} G</div>
          </div>
        )}
        <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
          <button className="btn btn-sm" onClick={onClose} style={{ background: "#E2E8F0" }}>Annuler</button>
          <button className="btn btn-sm btn-primary" onClick={onSubmit} data-testid="p2p-claim-submit">Informer le client</button>
        </div>
      </div>
    </div>
  );
}
